import React, { createContext, useState, useContext, useEffect } from "react";
import { useRuntimeConstants } from "../runtimeConstants/RuntimeConstantsContext";
import { useFeaturesReady } from "../featuresReady/FeaturesReadyContext";
import { getFeature } from "../../utils/features";
import { isProductUnavailable } from "../../../utils/productUtils";
import { Product } from "../../types/productTypes/product";
import {
  StylingLookDataFromServer,
  StylingLook,
} from "../../types/productTypes/stylingLookProduct";

interface WaysToStyleContextType {
  shouldShowWaysToStyle: boolean;
  stylingLooks: StylingLook[];
}

export const WaysToStyleContext = createContext<WaysToStyleContextType>({
  shouldShowWaysToStyle: false,
  stylingLooks: [],
});

export const isWaysToStyleFeatureEnabled = () =>
  getFeature("pdp-ways-to-style")?.isEnabled();

export const WaysToStyleProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { isMobile, product, stylingLooksData, isMixAndMatch } =
    useRuntimeConstants() as {
      isMobile: boolean;
      product: Product;
      stylingLooksData: StylingLookDataFromServer[];
      isMixAndMatch: boolean;
    };

  const [shouldShowWaysToStyle, setShouldShowWaysToStyle] = useState(false);
  const [stylingLooks, setStylingLooks] = useState<StylingLook[]>([]);
  const { areFeaturesReady } = useFeaturesReady() as {
    areFeaturesReady: boolean;
  };

  const [isWaysToStyleEnabled, setIsWaysToStyleEnabled] = useState(false);

  useEffect(() => {
    if (areFeaturesReady) {
      setIsWaysToStyleEnabled(isWaysToStyleFeatureEnabled());
    }
  }, [areFeaturesReady]);

  useEffect(() => {
    if (isMobile && areFeaturesReady && isWaysToStyleEnabled) {
      setShouldShowWaysToStyle(true);
    }
  }, [areFeaturesReady, isWaysToStyleEnabled, isMobile]);

  useEffect(() => {
    const updateStylingLooks = () => {
      const isUnavailable = isProductUnavailable(product);
      const shouldMapStylingLooks =
        stylingLooksData?.length && !isMixAndMatch && !isUnavailable;

      if (shouldMapStylingLooks) {
        const mappedLooks = mapStylingLooks(stylingLooksData);

        setStylingLooks(mappedLooks);
      }
    };

    if (areFeaturesReady && isWaysToStyleEnabled) {
      updateStylingLooks();
    }
  }, [
    areFeaturesReady,
    isWaysToStyleEnabled,
    stylingLooksData,
    product,
    isMixAndMatch,
  ]);

  return (
    <WaysToStyleContext.Provider
      value={{
        shouldShowWaysToStyle,
        stylingLooks,
      }}
    >
      {children}
    </WaysToStyleContext.Provider>
  );
};

const mapStylingLooks = (
  stylingLooksData: StylingLookDataFromServer[]
): StylingLook[] => {
  if (!stylingLooksData) {
    return [];
  }

  return stylingLooksData
    .map((look) => ({
      isHeroLook: look.isHeroLook,
      products:
        look.looksProductsFromServer || look.stylingLookProductsFromServer,
    }))
    .filter(
      (look): look is StylingLook =>
        look.products !== undefined && look.products.length > 0
    )
    .sort((a, b) => {
      return Number(b.isHeroLook) - Number(a.isHeroLook);
    });
};

export const useWaysToStyle = () => useContext(WaysToStyleContext);
